import { DeepPartial } from 'ts-essentials'
import { Address, Hash } from 'viem'
import { StateCreator } from 'zustand'

import { StoreState } from '.'
import { tryOrDefault } from '@/utils/tryOrDefault'

const SANDBOX_EXPIRY = 6 * 3600 * 1000 // 6 hours

export interface SandboxNetwork {
  name: string // will be displayed to the user in wallet UI
  forkUrl: string
  originChainId: number
  forkChainId: number
  createdAt: Date
  sandboxEnabled: boolean
  ephemeralAccountPrivateKey?: Hash
  ephemeralAccountAddress?: Address
}

export interface Sandbox {
  network: SandboxNetwork | undefined
  setNetwork: (
    network: SandboxNetwork | undefined | ((prev: SandboxNetwork | undefined) => SandboxNetwork | undefined),
  ) => void
}

export interface SandboxSlice {
  sandbox: Sandbox
}

// eslint-disable-next-line func-style
export const initSandboxSlice: StateCreator<StoreState, [], [], SandboxSlice> = (set) => ({
  sandbox: {
    network: undefined,
    setNetwork: (
      network: SandboxNetwork | undefined | ((prev: SandboxNetwork | undefined) => SandboxNetwork | undefined),
    ) =>
      set((state) => {
        const newNetwork = typeof network === 'function' ? network(state.sandbox.network) : network
        return { sandbox: { network: newNetwork, setNetwork: state.sandbox.setNetwork } }
      }),
  },
})

export interface PersistedSandboxSlice {
  sandbox: {
    network?: {
      name: string
      forkUrl: string
      originChainId: number
      forkChainId: number
      createdAt: string
      ephemeralAccountPrivateKey?: Hash
      ephemeralAccountAddress?: Address
    }
  }
}

export function persistSandboxSlice(state: StoreState): PersistedSandboxSlice {
  return {
    sandbox: {
      network: state.sandbox.network && {
        name: state.sandbox.network.name,
        forkUrl: state.sandbox.network.forkUrl,
        originChainId: state.sandbox.network.originChainId,
        forkChainId: state.sandbox.network.forkChainId,
        createdAt: state.sandbox.network.createdAt.toISOString(),
        ephemeralAccountPrivateKey: state.sandbox.network.ephemeralAccountPrivateKey,
        ephemeralAccountAddress: state.sandbox.network.ephemeralAccountAddress,
        // sandboxEnabled is intentionally excluded from persistence
      },
    },
  }
}

export function unPersistSandboxSlice(persistedState: DeepPartial<PersistedSandboxSlice>): DeepPartial<SandboxSlice> {
  if (
    !persistedState.sandbox?.network ||
    (import.meta.env.VITE_FEATURE_CLEAR_STALE_SANDBOX === '1' && isPersistedSandboxExpired(persistedState))
  ) {
    return {}
  }

  return {
    sandbox: {
      network: {
        ...persistedState.sandbox.network,
        createdAt: tryOrDefault(() => {
          const date = persistedState.sandbox?.network?.createdAt

          return date ? new Date(date) : undefined
        }, undefined),
        sandboxEnabled: false,
      },
    },
  }
}

function isPersistedSandboxExpired(persistedState: DeepPartial<PersistedSandboxSlice>): boolean {
  const createdAt = tryOrDefault(() => {
    const createdAt = persistedState?.sandbox?.network?.createdAt
    return createdAt ? new Date(createdAt) : undefined
  }, undefined)

  if (createdAt === undefined) {
    return true
  }

  const now = new Date()
  return now.getTime() - createdAt.getTime() > SANDBOX_EXPIRY
}
